import { GlobalStore, GridStore } from "@roc/feature-app-core";
import { DrawDqDataInterface } from "@roc/feature-types";
import { getUniqueId, isNil } from "@roc/feature-utils";
import { action, flow, makeObservable, observable } from "mobx";
import EditDqDataStore from "./editDqDataStore";

interface SummaryInformation {
  totalDrawAmount: number;
  wireFees: number;
  inspectionFees: number;
  otherReceivablesFees: number;
  totalDqAmount: number;
  netWireAmount: number;
};

interface BankInfo {
  accountName: string;
  accountNumber: string;
  bankName: string;
  city: string;
  routingNumber: string;
  state: string;
  streetName: string;
  zipCode: string;
  ffcAccountName: string;
  ffcAccountNumber: string;
  ffcBankName: string;
  ffcRoutingNumber: string;
};

interface ApprovedDrawsData {
  drawId: number;
  drawNumber: number;
  propertyAddress: string;
  withheldInspectionFees: number;
  withheldWireFees: number;
  otherReceivablesFees: number;
  amount: number;
  stage: string;
}
export abstract class SendWireBaseStore {
  protected globalStore: GlobalStore;
  editDQDataStore: EditDqDataStore;
  drawDQDataGridStore: GridStore;
  consolidateDrawsGridStore: GridStore;
  drawId: string;
  loanId: string;
  gridDQData: any;
  drawData: any;
  selectedDQItems: DrawDqDataInterface[];
  summaryInformation: SummaryInformation;
  readOnlyMode: boolean;
  isApplyDQConfirmDialogOpen: boolean;
  wireInfo: any;

  abstract init();
  abstract fetchAllData(drawIds: number[]);
  abstract fetchDqData(id);
  abstract fetchDrawById(id);

  selectedDrawItems: ApprovedDrawsData[];
  gridApprovedDrawsData: any;
  abstract fetchDrawsData(loanId: string);
  abstract fetchApprovedDraws(loanId);
  abstract sendWire();
  abstract applyDQData();
  abstract sendInterestPaymentWire();
  abstract getWireDetails(wireId: number);
  abstract handleWire();

  public activeStep: number;

  bankInformation: BankInfo;

  constructor(globalStore: GlobalStore) {
    this.globalStore = globalStore;
    this.editDQDataStore = new EditDqDataStore(globalStore);

    makeObservable(this, {
      activeStep: observable,
      moveToStep: flow,
      goNextStep: action,
      goPrevStep: action,
      drawId: observable,
      summaryInformation: observable,
      selectedDQItems: observable,
      calculateFunds: action,
      selectedDQRow: action,
      setLoanDqDataEdit: action,
      refreshDQRows: action,
      selectedDrawItems: observable,
      drawData: observable,
      bankInformation: observable,
      selectedDrawRow: action,
      sendWire: flow,
      loanId: observable,
      applyDQData: flow,
      sendInterestPaymentWire: flow,
      init: flow,
      readOnlyMode: observable,
      isApplyDQConfirmDialogOpen: observable,
      setIsApplyDQConfirmDialogOpen: action,
      wireInfo: observable,
      getWireDetails: flow,
      handleWire: flow,
    });
    this.setDefaults()
  }

  setDefaults() {
    this.activeStep = 0;
    this.drawId = "";
    this.selectedDQItems = [];
    this.selectedDrawItems = [];
    this.summaryInformation = null;
    this.drawData = [];
    this.bankInformation = null;
    this.loanId = null;
    this.readOnlyMode = false;
    this.wireInfo = null;
  }

  *moveToStep(step) {
    this.activeStep = step;
  }

  goPrevStep() {
    this.moveToStep(this.activeStep - 1);
  }

  goNextStep() {
    this.moveToStep(this.activeStep + 1);
  }

  setIsApplyDQConfirmDialogOpen(isApplyDQConfirmDialogOpen: boolean) {
    this.isApplyDQConfirmDialogOpen = isApplyDQConfirmDialogOpen;
  }

  processData = () => {
    this.drawDQDataGridStore.gridData.data.rows = this.drawDQDataGridStore.gridData.data.rows.map(row =>
      this.processItem(row)
    );
  }

  processItem(row) {
    const updatedRow = {
      ...row,
      tempId: getUniqueId(),
      disabled: !isNil(row.drawsDqDataId),
    };
    if (updatedRow.applied) {
      const exists = this.selectedDQItems.some(item => item.loanId === updatedRow.loanId);
      if (!exists) {
        this.selectedDQItems.push(updatedRow);
        this.calculateFunds();
      }
    }
    return updatedRow;
  }

  calculateFunds() {
    const totalDrawAmount = this.selectedDrawItems ? this.selectedDrawItems.reduce((total, item) => total + (item.amount || 0), 0) : 0;
    const totalDqAmount = this.selectedDQItems ? this.selectedDQItems.reduce((total, item) => total + (item.appliedDqAmount || 0), 0) : 0;
    const inspectionFees = this.selectedDrawItems ? this.selectedDrawItems.reduce((total, item) => total + (item.withheldInspectionFees || 0), 0) : 0;
    const otherReceivablesFees = this.selectedDrawItems ? this.selectedDrawItems.reduce((total, item) => total + (item.otherReceivablesFees || 0), 0) : 0;
    const wireFees = this.selectedDrawItems ? this.selectedDrawItems.reduce((total, item) => total + (item.withheldWireFees || 0), 0) : 0;
    const netWireAmount = totalDrawAmount - totalDqAmount - inspectionFees - wireFees - otherReceivablesFees;

    this.summaryInformation = {
      totalDrawAmount: totalDrawAmount,
      totalDqAmount: totalDqAmount,
      wireFees: wireFees,
      inspectionFees: inspectionFees,
      otherReceivablesFees: otherReceivablesFees,
      netWireAmount: netWireAmount
    };
  }

  selectedDQRow(row: DrawDqDataInterface) {
    const index = this.selectedDQItems.findIndex(item => item.tempId === row.tempId);
    if (index > -1) {
      this.updateDQRows({ ...row, applied: false, isSelected: false });
      this.selectedDQItems.splice(index, 1);
    } else {
      const updatedRow = { ...row, applied: true };
      this.updateDQRows(updatedRow);
      this.selectedDQItems.push(updatedRow);
    }

    this.calculateFunds();
  }

  updateDQRows(updatedItem) {
    const rows = this.drawDQDataGridStore.gridData.data.rows as Array<any>;
    const updatedRows = rows.map(row => {
      if (row.tempId === updatedItem.tempId) {
        return {
          ...row,
          ...updatedItem
        }
      }
      return row;
    });
    this.drawDQDataGridStore.setData(updatedRows);
  }

  refreshDQRows() {
    const rows = this.drawDQDataGridStore.gridData.data.rows as Array<any>;
    this.drawDQDataGridStore.setData(rows);
  }

  setLoanDqDataEdit() {
    const formValues = this.editDQDataStore.getFormValues();
    if (formValues.appliedDqAmount <= formValues.dqAmount) {
      const updatedItem = { ...formValues };
      this.updateDQSelectedItems(updatedItem);
      this.updateDQRows(updatedItem);
      this.calculateFunds();
    } else {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Applied amount can not be greatter than delinquent amount.',
      });
    }
  }

  updateDQSelectedItems(updatedItem) {
    const index = this.selectedDQItems.findIndex(item => item.tempId === updatedItem.tempId);
    if (index > -1) {
      this.selectedDQItems[index].dqAmount = updatedItem.dqAmount;
      this.selectedDQItems[index].appliedDqAmount = updatedItem.appliedDqAmount;
      this.selectedDQItems[index].comments = updatedItem.comments;
    }
  }

  selectedDrawRow(row: ApprovedDrawsData) {
    const existingIndex = this.selectedDrawItems.findIndex(item => item.drawId === row.drawId);

    if (existingIndex !== -1) {
      this.selectedDrawItems.splice(existingIndex, 1);
    } else {
      this.selectedDrawItems.push(row);
    }
    this.drawDQDataGridStore.fetchGridData();

    this.calculateFunds();
  }

  processDrawData = () => {
    this.consolidateDrawsGridStore.gridData.data.rows = this.consolidateDrawsGridStore.gridData.data.rows.map(row =>
      this.processDrawItem(row)
    );
    this.readOnlyMode = this.selectedDrawItems.some(drawItem => drawItem.stage === 'Wire Being Prepared');
  }

  processDrawItem(row) {
    this.selectedDrawItems.push(row);
    this.calculateFunds();
    if (row.drawId === parseInt(this.drawId, 10)) {
      return { ...row, disabled: true }
    }
    return { ...row, disabled: false };
  }
}